import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { initialsFromName } from '../../../utils';
import { brandLogoSources } from '../../../utils/brandLogo';
import { FlagChip } from '../../../components/videoSection/StatusPill';
import { brandIsNetwork, planLabel } from '../../../utils/plans';

interface BrandCardProps {
  logo?: string;
  cover_image?: string;
  name: string;
  slug?: string;
  rmn?: boolean;
  plan?: string | null;
  video_count?: number;
  product_count?: number;
  children: ReactNode;
  onClick?: () => void;
  // Optional strip under the name (internal Brands page: scores, last
  // reports and the Run buttons). Clicks inside it must not open the card.
  footer?: ReactNode;
}

export function BrandCard({
  logo,
  cover_image,
  name,
  slug,
  rmn,
  plan,
  video_count,
  product_count,
  children,
  onClick,
  footer,
}: BrandCardProps) {
  const sources = useMemo(
    () => brandLogoSources({ logo, cover_image, name }),
    [logo, cover_image, name]
  );
  const [sourceIndex, setSourceIndex] = useState(0);

  useEffect(() => setSourceIndex(0), [sources]);

  const logoUrl = sources[sourceIndex];
  const hasCounts = video_count != null || product_count != null;
  const videos = video_count ?? 0;
  const products = product_count ?? 0;

  return (
    <div
      className={classNames(
        'bg-surface border border-border-token rounded-xl overflow-hidden flex flex-col h-full box-border transition-colors duration-150 hover:border-accent',
        { 'cursor-pointer': !!onClick }
      )}
      style={{ boxShadow: 'var(--sh-1)', width: '100%' }}
      onClick={onClick}
      title={name}
    >
      <div className="flex flex-col flex-1 min-h-0" style={{ gap: 12, padding: '13px 15px 15px' }}>
        <div className="flex items-start gap-2.5">
          <span className="w-12 h-12 shrink-0 rounded-lg bg-surface-2 text-accent-text inline-flex items-center justify-center overflow-hidden font-bold" style={{ fontSize: 15.5 }}>
            {logoUrl ? (
              <img
                src={logoUrl}
                alt=""
                loading="lazy"
                className="w-full h-full object-contain"
                onError={() => setSourceIndex((i) => i + 1)}
              />
            ) : (
              initialsFromName(name)
            )}
          </span>
          <span className="flex-1 min-w-0 flex items-center flex-wrap gap-[7px]">
            {/* Plan badge (free plans show nothing); network plans in gold. */}
            {plan && plan !== 'free' && (
              <FlagChip gold={brandIsNetwork({ rmn, plan })} compact label={planLabel(plan)} />
            )}
          </span>
          <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
            {children}
          </div>
        </div>

        <div>
          <div
            className="font-bold text-text-1"
            style={{
              fontSize: 15.5,
              lineHeight: 1.3,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {name}
          </div>
          {slug && (
            <div className="text-text-2 truncate" style={{ marginTop: 5, fontSize: 11.5 }}>
              {slug}
            </div>
          )}
        </div>

        {hasCounts && (
          <div className="flex items-center border-t border-border-token mt-auto" style={{ gap: 12, paddingTop: 11 }}>
            <b className="text-text-1 font-bold tabular-nums" style={{ fontSize: 12.5 }}>
              {videos}{' '}
              <span className="text-text-2">Video{videos === 1 ? '' : 's'}</span>
            </b>
            <i className="w-px h-3.5 shrink-0 bg-border-token" />
            <b className="text-text-1 font-bold tabular-nums" style={{ fontSize: 12.5 }}>
              {products}{' '}
              <span className="text-text-2">Product{products === 1 ? '' : 's'}</span>
            </b>
          </div>
        )}
        {footer ? (
          <div
            className={classNames('border-t border-border-token', { 'mt-auto': !hasCounts })}
            style={{ paddingTop: 11 }}
            onClick={(e) => e.stopPropagation()}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}
